import { inject, Injectable } from '@angular/core';
import type { PreloadingStrategy, Route } from '@angular/router';

import { IS_SERVER } from '@rpgc-ai/shared-interfaces/tokens/is-server.token';
import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import { catchError, defer, EMPTY, finalize, Observable, type Subscriber, type Subscription, timeout } from 'rxjs';

/** Минимальная форма Network Information API (не во всех lib.dom, нет на iOS Safari). */
interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

/** `requestIdleCallback` ещё не во всех TS lib.dom — описываем точечно. */
interface IdleWindow {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
}

/**
 * Жизненный цикл таски в serial-очереди:
 *   queued  — в #queue, ждёт своей очереди (drain-слот занят другой таской).
 *   idle    — извлечена из #queue, занимает drain-слот, ждёт requestIdleCallback.
 *   running — load() подписан, идёт прогрев чанка.
 *   done    — завершилась / отменена.
 * Состояние нужно teardown'у: отмена `idle`-таски обязана сама продвинуть очередь
 * (finalize не сработает — load ещё не подписан), иначе очередь зависает навсегда.
 */
type TaskState = 'queued' | 'idle' | 'running' | 'done';

interface PreloadTask {
  load: () => Observable<unknown>;
  subscriber: Subscriber<unknown>;
  state: TaskState;
  cancelIdle?: () => void;
  innerSub?: Subscription;
}

const IDLE_TIMEOUT_MS = 2000;
const FALLBACK_DELAY_MS = 1500;
/** Висящий chunk-fetch на плохой сети не должен застопорить очередь навсегда. */
const LOAD_TIMEOUT_MS = 15000;
const SLOW_EFFECTIVE_TYPES = new Set(['slow-2g', '2g']);

/**
 * Idle-aware preloading: прогревает lazy-чанки роутов, ПОМЕЧЕННЫХ `data: { preload: true }`,
 * в `requestIdleCallback` (когда мейн-тред свободен, после первого рендера).
 *
 * Поведение:
 *   - Прелоадит ТОЛЬКО роуты с `data.preload === true` (opt-in). Остальные → on-demand.
 *   - Серийная очередь: чанки тянутся по одному (следующий после резолва/таймаута/отмены
 *     предыдущего) — без bandwidth-спайка на мобиле.
 *   - Per-load timeout ({@link LOAD_TIMEOUT_MS}, `first`+`each`): висящий чанк (нет первой
 *     эмиссии ИЛИ эмитнул, но не комплитится) не блокирует остальные.
 *   - `defer(load)` + `catchError`: sync-throw и async-error/timeout глушатся (НЕ наружу) —
 *     Angular RouterPreloader подписан без `catchError`, error убил бы preload навсегда.
 *   - Network-aware: skip при `saveData` / `effectiveType` slow-2g/2g (где API доступен).
 *   - SSR-safe: на сервере возвращает EMPTY.
 *
 * ⚠️ Помечать `preload: true` стоит ТОЛЬКО leaf-роуты (`loadComponent`). Вложенные `loadChildren`
 *    рекурсивно дёргают preload детей в эту же serial-очередь → возможен deadlock/задержка.
 *
 * Подключается через `withPreloading` в web-front, rpgc и TMA. Сборка Capacitor
 * остаётся на дефолтном `NoPreloading`: её чанки локальные, прогревать нечего.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloadStrategy implements PreloadingStrategy {
  readonly #window = inject(WINDOW);
  readonly #isServer = inject(IS_SERVER);

  readonly #queue: PreloadTask[] = [];
  #draining = false;

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (this.#isServer || route.data?.['preload'] !== true || this.#shouldSkipForNetwork()) {
      return EMPTY;
    }

    return new Observable<unknown>((subscriber) => {
      const task: PreloadTask = { load, subscriber, state: 'queued' };
      this.#queue.push(task);
      this.#drain();

      return () => this.#teardown(task);
    });
  }

  #teardown(task: PreloadTask): void {
    if (task.state === 'done') {
      return;
    }
    const wasIdle = task.state === 'idle';
    task.state = 'done';

    task.cancelIdle?.();
    // running → unsubscribe триггерит finalize → #scheduleNext (advance).
    task.innerSub?.unsubscribe();

    const index = this.#queue.indexOf(task);
    if (index >= 0) {
      this.#queue.splice(index, 1);
    }

    // idle-таска занимала drain-слот, но load ещё не подписан → finalize НЕ сработает.
    // Продвигаем очередь вручную, иначе #draining навсегда true и preloading мёртв.
    if (wasIdle) {
      this.#scheduleNext();
    }
  }

  #drain(): void {
    if (this.#draining) {
      return;
    }
    this.#draining = true;
    this.#scheduleNext();
  }

  /**
   * Продвигаем очередь ВСЕГДА через microtask. Иначе синхронно-завершающийся `load()`
   * (прогретый кеш → sync complete) рекурсивно звал бы `#pumpNext` → переполнение стека
   * на пачке помеченных роутов.
   */
  #scheduleNext(): void {
    queueMicrotask(() => this.#pumpNext());
  }

  #pumpNext(): void {
    const task = this.#queue.shift();
    if (!task) {
      this.#draining = false;
      return;
    }
    if (task.state === 'done') {
      this.#scheduleNext();
      return;
    }
    task.state = 'idle';
    task.cancelIdle = this.#whenIdle(() => this.#runTask(task));
  }

  #runTask(task: PreloadTask): void {
    if (task.state === 'done') {
      this.#scheduleNext();
      return;
    }
    task.state = 'running';

    task.innerSub = defer(task.load)
      .pipe(
        // first — нет первой эмиссии; each — эмитнул, но висит без complete. Оба → timeout.
        timeout({ first: LOAD_TIMEOUT_MS, each: LOAD_TIMEOUT_MS }),
        // Глушим ошибку/таймаут/sync-throw — НЕ эмитим наружу (см. JSDoc класса).
        catchError(() => EMPTY),
        // Гарантирует продвижение очереди при любом исходе (complete / error / timeout / unsubscribe).
        finalize(() => {
          task.state = 'done';
          this.#scheduleNext();
        }),
      )
      .subscribe({
        next: (value) => {
          if (!task.subscriber.closed) {
            task.subscriber.next(value);
          }
        },
        complete: () => {
          if (!task.subscriber.closed) {
            task.subscriber.complete();
          }
        },
      });
  }

  #whenIdle(callback: () => void): () => void {
    const win = this.#window as (Window & IdleWindow) | null;
    if (win?.requestIdleCallback) {
      const handle = win.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
      return () => win.cancelIdleCallback?.(handle);
    }
    // Нет requestIdleCallback (старые iOS WKWebView) → setTimeout-fallback. Задержка
    // консервативная (не 0), чтобы не конкурировать с initial render / Telegram SDK.
    const handle = globalThis.setTimeout(callback, FALLBACK_DELAY_MS);
    return () => globalThis.clearTimeout(handle);
  }

  #shouldSkipForNetwork(): boolean {
    const navigator = this.#window?.navigator as (Navigator & { connection?: NetworkInformationLike }) | undefined;
    const connection = navigator?.connection;
    if (!connection) {
      return false;
    }
    return connection.saveData === true || (connection.effectiveType !== undefined && SLOW_EFFECTIVE_TYPES.has(connection.effectiveType));
  }
}
